import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Typography,
} from '@mui/material';
import { AppButton, AppTextField } from '../../design-system';
import { efemService } from '../../../services/EfemService';
import type { OpsDeviceDetail } from '@cnapse/shared/schemas.js';
import * as dt from '@cnapse/shared/data.types.js';
import { PRODUCT_NAMES } from '@cnapse/shared';
import { opsDevicesService } from '../../../services/OpsDevicesService';
import { tenantDevicesService } from '../../../services/TenantDevicesService';

const PERIPHERAL_CATEGORIES = new Set(['barcode-scanner', 'handheld-barcode-scanner', 'waferid-camera']);

export interface EfemGatewayOption {
  serialNumber: string;
  label: string;
  /** Tenant EFEM client id — used to resolve load port ids from hardware / stored profile. */
  gatewaySerialNumber?: string;
}

interface EfemInstallationCardProps {
  device: OpsDeviceDetail;
  portal: 'provider' | 'tenant';
  gateways: EfemGatewayOption[];
  onUpdated: () => void;
}

export function EfemInstallationCard(props: EfemInstallationCardProps) {
  const category = props.device.deviceCategory ?? '';
  if (!PERIPHERAL_CATEGORIES.has(category)) return null;
  return <EfemInstallationCardContent {...props} category={category} />;
}

interface EfemInstallationCardContentProps extends EfemInstallationCardProps {
  category: string;
}

function EfemInstallationCardContent({
  device,
  portal,
  gateways,
  onUpdated,
  category,
}: EfemInstallationCardContentProps) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const peripheral = device.efemPeripheral as Record<string, unknown> | null | undefined;

  const [gatewaySerial, setGatewaySerial] = useState(
    (peripheral?.parentGatewaySerialNumber as string) ?? '',
  );
  const [displayLabel, setDisplayLabel] = useState((peripheral?.displayLabel as string) ?? '');
  const [mountCalibrationDeg, setMountCalibrationDeg] = useState(
    String(peripheral?.mountCalibrationDeg ?? 0),
  );
  const [supportedWaferSize, setSupportedWaferSize] = useState(
    (peripheral?.supportedWaferSize as string) ?? dt.WAFER_SIZE.Size300mm,
  );
  const [idSide, setIdSide] = useState<'top' | 'bottom'>(
    (peripheral?.idSide as 'top' | 'bottom') ?? 'bottom',
  );
  const [loadPortId, setLoadPortId] = useState(String(peripheral?.loadPortId ?? ''));
  const [loadPortOptions, setLoadPortOptions] = useState<Array<{ id: number; size: string }>>([]);
  const [loadPortsLoading, setLoadPortsLoading] = useState(false);

  const selectedGateway = gateways.find((g) => g.serialNumber === gatewaySerial);

  useEffect(() => {
    if (category !== 'barcode-scanner' || portal !== 'tenant' || !selectedGateway?.gatewaySerialNumber) {
      setLoadPortOptions([]);
      return;
    }
    let cancelled = false;
    setLoadPortsLoading(true);
    void efemService
      .getEfemHardwareProfile(selectedGateway.gatewaySerialNumber)
      .then((view) => {
        if (!cancelled) {
          const ports = view.hardwareProfile?.loadPorts ?? [];
          setLoadPortOptions(ports);
          if (!loadPortId && ports[0] !== undefined) setLoadPortId(String(ports[0].id));
        }
      })
      .catch(() => {
        if (!cancelled) setLoadPortOptions([]);
      })
      .finally(() => {
        if (!cancelled) setLoadPortsLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // loadPortId intentionally omitted — only seed default when gateway changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category, portal, selectedGateway?.gatewaySerialNumber, gatewaySerial]);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const binding =
        category === 'waferid-camera'
          ? {
              parentGatewaySerialNumber: gatewaySerial,
              displayLabel: displayLabel || undefined,
              supportedWaferSize,
              idSide,
              mountCalibrationDeg: Number(mountCalibrationDeg),
            }
          : category === 'handheld-barcode-scanner'
            ? {
                parentGatewaySerialNumber: gatewaySerial,
                displayLabel: displayLabel || undefined,
              }
            : {
                parentGatewaySerialNumber: gatewaySerial,
                displayLabel: displayLabel || undefined,
                loadPortId: Number(loadPortId),
              };

      if (portal === 'tenant') {
        await tenantDevicesService.patchTenantEfemPeripheral(device.serialNumber, binding);
      } else {
        await opsDevicesService.updateNonIotDevice(device.serialNumber, { efemPeripheral: binding });
      }
      setOpen(false);
      onUpdated();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleClear = async () => {
    if (!window.confirm(`Clear ${PRODUCT_NAMES.AXON} installation binding for this device?`)) return;
    setSaving(true);
    setError(null);
    try {
      if (portal === 'tenant') {
        await tenantDevicesService.patchTenantEfemPeripheral(device.serialNumber, null);
      } else {
        await opsDevicesService.updateNonIotDevice(device.serialNumber, { efemPeripheral: null });
      }
      onUpdated();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Clear failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card variant="outlined" sx={{ mb: 2 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          {PRODUCT_NAMES.AXON} installation
        </Typography>
        {peripheral ? (
          <Box>
            <Typography variant="body2">
              <strong>{String(peripheral.displayLabel ?? device.serialNumber)}</strong>
            </Typography>
            <Typography variant="body2" color="text.secondary" fontFamily="monospace">
              {PRODUCT_NAMES.AXON}: {String(peripheral.parentGatewaySerialNumber)}
            </Typography>
            {category === 'waferid-camera' && (
              <Typography variant="body2" color="text.secondary">
                {String(peripheral.supportedWaferSize)} · ID {String(peripheral.idSide)} · cal{' '}
                {String(peripheral.mountCalibrationDeg)}°
              </Typography>
            )}
            {category === 'barcode-scanner' && (
              <Typography variant="body2" color="text.secondary">
                Load port {String(peripheral.loadPortId)}
              </Typography>
            )}
            {category === 'handheld-barcode-scanner' && (
              <Typography variant="body2" color="text.secondary">
                Handheld scanner — gateway only (load port chosen at bind time)
              </Typography>
            )}
          </Box>
        ) : (
          <Alert severity="warning" sx={{ mb: 1 }}>
            Not commissioned for a {PRODUCT_NAMES.AXON}. Batch scan and load-port barcode commands will fail until
            installation is configured.
          </Alert>
        )}
        {error && (
          <Alert severity="error" sx={{ mt: 1 }}>
            {error}
          </Alert>
        )}
        <Box display="flex" gap={1} mt={2}>
          <AppButton variant="contained" size="small" onClick={() => setOpen(true)}>
            Configure installation
          </AppButton>
          {peripheral && (
            <AppButton variant="outlined" color="warning" size="small" onClick={() => void handleClear()} disabled={saving}>
              Clear installation
            </AppButton>
          )}
        </Box>
      </CardContent>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Configure {PRODUCT_NAMES.AXON} installation</DialogTitle>
        <DialogContent>
          <AppTextField
            select
            fullWidth
            margin="normal"
            label={PRODUCT_NAMES.AXON}
            value={gatewaySerial}
            onChange={(e) => setGatewaySerial(e.target.value)}
          >
            {gateways.map((g) => (
              <MenuItem key={g.serialNumber} value={g.serialNumber}>
                {g.label}
              </MenuItem>
            ))}
          </AppTextField>
          <AppTextField
            fullWidth
            margin="normal"
            label="Device name"
            value={displayLabel}
            onChange={(e) => setDisplayLabel(e.target.value)}
            placeholder={
              category === 'barcode-scanner'
                ? 'Load port 1 scanner'
                : category === 'handheld-barcode-scanner'
                  ? 'Handheld scanner'
                  : '300 mm bottom camera'
            }
          />
          {category === 'waferid-camera' && (
            <>
              <AppTextField
                select
                fullWidth
                margin="normal"
                label="Wafer size"
                value={supportedWaferSize}
                onChange={(e) => setSupportedWaferSize(e.target.value)}
              >
                {Object.values(dt.WAFER_SIZE).map((s) => (
                  <MenuItem key={s} value={s}>
                    {s}
                  </MenuItem>
                ))}
              </AppTextField>
              <AppTextField
                select
                fullWidth
                margin="normal"
                label="ID side"
                value={idSide}
                onChange={(e) => setIdSide(e.target.value as 'top' | 'bottom')}
              >
                <MenuItem value="top">Top</MenuItem>
                <MenuItem value="bottom">Bottom</MenuItem>
              </AppTextField>
              <AppTextField
                fullWidth
                margin="normal"
                label="Mount calibration (°)"
                type="number"
                value={mountCalibrationDeg}
                onChange={(e) => setMountCalibrationDeg(e.target.value)}
              />
            </>
          )}
          {category === 'barcode-scanner' && (
            <>
              {portal === 'tenant' && selectedGateway?.gatewaySerialNumber && loadPortOptions.length === 0 && !loadPortsLoading && (
                <Alert severity="warning" sx={{ mt: 1 }}>
                  No load ports configured for this {PRODUCT_NAMES.AXON}. Tenant admin must set load port ids and sizes under
                  Device Claims → Axon hardware profile before binding a scanner.
                </Alert>
              )}
              {loadPortOptions.length > 0 ? (
                <AppTextField
                  select
                  fullWidth
                  margin="normal"
                  label="Load port"
                  value={loadPortId}
                  onChange={(e) => setLoadPortId(e.target.value)}
                  disabled={loadPortsLoading}
                  helperText={`Must match a load port on this ${PRODUCT_NAMES.AXON} (from hardware or device profile).`}
                >
                  {loadPortOptions.map((p) => (
                    <MenuItem key={p.id} value={String(p.id)}>
                      {p.id} ({p.size})
                    </MenuItem>
                  ))}
                </AppTextField>
              ) : (
                <AppTextField
                  fullWidth
                  margin="normal"
                  label="Load port"
                  type="number"
                  inputProps={{ min: 1 }}
                  value={loadPortId}
                  onChange={(e) => setLoadPortId(e.target.value)}
                  helperText={
                    portal === 'tenant' && selectedGateway?.gatewaySerialNumber
                      ? 'Resolve ports from hardware when the gateway is online, or enter the id from the device profile.'
                      : 'Enter the hardware load port id for this scanner mount.'
                  }
                />
              )}
            </>
          )}
          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <AppButton onClick={() => setOpen(false)}>Cancel</AppButton>
          <AppButton
            variant="contained"
            onClick={() => void handleSave()}
            disabled={saving || !gatewaySerial || (category === 'barcode-scanner' && !loadPortId)}
          >
            Save
          </AppButton>
        </DialogActions>
      </Dialog>
    </Card>
  );
}
